/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-11
 */

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Chip,
  Tooltip,
  Box,
  Skeleton,
} from '@mui/material';
import {
  PersonOutline as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  Star as OwnerIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import { StudioRole } from '../../../types/studio';
import { StudioMember } from '../../../types/studioMembers';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import MemberActionMenu from './MemberActionMenu';

interface MemberTableProps {
  members: StudioMember[];
  loading?: boolean;
  onRoleChange: (
    membershipId: string,
    role: StudioRole,
    reason?: string
  ) => void;
  onRemoveMember: (membershipId: string, reason?: string) => void;
  onSendMessage: (userId: string) => void;
}

interface FieldVisibility {
  showEmail: boolean;
  showPhone: boolean;
}

const MemberTable = ({
  members,
  loading = false,
  onRoleChange,
  onRemoveMember,
  onSendMessage,
}: MemberTableProps) => {
  const { t } = useTranslation('studio');
  const navigate = useNavigate();
  const {
    role: currentUserRole,
    studioId,
    permissions,
  } = useStudioPermissions();

  // Check if current user can manage members (Owner/Manager only)
  const canManageMembers = permissions?.canManageMembers || false;

  const getFieldVisibility = (member: StudioMember): FieldVisibility => {
    const isOwnerOrManager = !!(
      currentUserRole && ['owner', 'manager'].includes(currentUserRole)
    );

    // Get privacy settings from the user profile
    const profile = member.user?.profile;

    // If no user (pending invitation) or no profile, default to private
    if (!member.user || !profile) {
      return {
        showEmail: isOwnerOrManager, // Owners/managers can see invitation emails
        showPhone: false, // No phone for pending invitations
      };
    }

    return {
      showEmail: isOwnerOrManager || (profile.email_visible ?? false),
      showPhone: isOwnerOrManager || (profile.phone_visible ?? false),
    };
  };

  const getRoleIcon = (role: StudioRole) => {
    switch (role) {
      case StudioRole.OWNER:
        return <OwnerIcon fontSize="small" sx={{ color: '#FFD700' }} />;
      case StudioRole.MANAGER:
        return <ManagerIcon fontSize="small" sx={{ color: '#0d47a1' }} />;
      case StudioRole.ADMIN:
        return <AdminIcon fontSize="small" sx={{ color: '#1976d2' }} />;
      case StudioRole.MEMBER:
        return <PersonIcon fontSize="small" sx={{ color: '#666' }} />;
      default:
        return <PersonIcon fontSize="small" sx={{ color: '#666' }} />;
    }
  };

  const getRoleColor = (role: StudioRole) => {
    switch (role) {
      case StudioRole.OWNER:
        return '#FFD700';
      case StudioRole.MANAGER:
        return '#0d47a1';
      case StudioRole.ADMIN:
        return '#1976d2';
      case StudioRole.MEMBER:
        return '#666';
      default:
        return '#666';
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    if (!firstName || !lastName) return '?';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    // Basic international phone formatting
    // This could be enhanced with a proper phone formatting library
    return phone.replace(/(\+\d{1,3})(\d{3})(\d{3})(\d{4})/, '$1 $2-$3-$4');
  };

  // Used by both a row click and the actions menu's "View Profile"
  const openMemberProfile = (member: StudioMember) => {
    if (studioId && member.user) {
      navigate(`/studio/${studioId}/members/${member.id}`);
    }
  };

  const handleRowClick = (member: StudioMember, event: React.MouseEvent) => {
    // Don't navigate if the click was on the actions menu area
    if ((event.target as HTMLElement).closest('[data-actions-menu]')) {
      return;
    }

    openMemberProfile(member);
  };

  // Check if current user is the member being displayed (for owner self-protection)
  const isCurrentUser = (member: StudioMember) =>
    // This would need to be implemented based on how we identify the current user
    // For now, we'll use a simple role check - if current user is owner and member is owner
    currentUserRole === 'owner' && member.role === 'owner';
  // Determine if actions menu should be shown for this member
  const shouldShowActionsMenu = (member: StudioMember) => {
    // Only show for Owner/Manager users
    if (!canManageMembers) return false;

    // Don't show actions menu for current user (owner self-protection)
    if (isCurrentUser(member)) return false;

    return true;
  };

  const renderPrivateField = (
    value: string,
    isVisible: boolean,
    formatFn?: (val: string) => string
  ) => {
    if (isVisible && value) {
      return (
        <Typography variant="body2">
          {formatFn ? formatFn(value) : value}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={t('memberTable.privateInfo')}>
          <LockIcon fontSize="small" color="disabled" />
        </Tooltip>
        <Typography variant="body2" color="text.disabled">
          {t('memberTable.private')}
        </Typography>
      </Box>
    );
  };

  // Loading skeleton
  if (loading) {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('memberTable.headers.avatar')}</TableCell>
              <TableCell>{t('memberTable.headers.firstName')}</TableCell>
              <TableCell>{t('memberTable.headers.lastName')}</TableCell>
              <TableCell>{t('memberTable.headers.role')}</TableCell>
              <TableCell>{t('memberTable.headers.title')}</TableCell>
              <TableCell>{t('memberTable.headers.email')}</TableCell>
              <TableCell>{t('memberTable.headers.phone')}</TableCell>
              <TableCell align="right">
                {t('memberTable.headers.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 5 }, (_, index) => (
              <TableRow key={`skeleton-${index}`}>
                <TableCell>
                  <Skeleton variant="circular" width={32} height={32} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={80} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={100} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="rectangular" width={60} height={24} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={120} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={150} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={120} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="circular" width={24} height={24} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Empty state
  if (!members || members.length === 0) {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('memberTable.headers.avatar')}</TableCell>
              <TableCell>{t('memberTable.headers.firstName')}</TableCell>
              <TableCell>{t('memberTable.headers.lastName')}</TableCell>
              <TableCell>{t('memberTable.headers.role')}</TableCell>
              <TableCell>{t('memberTable.headers.title')}</TableCell>
              <TableCell>{t('memberTable.headers.email')}</TableCell>
              <TableCell>{t('memberTable.headers.phone')}</TableCell>
              <TableCell align="right">
                {t('memberTable.headers.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('memberTable.empty.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('memberTable.empty.description')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t('memberTable.headers.avatar')}</TableCell>
            <TableCell>{t('memberTable.headers.firstName')}</TableCell>
            <TableCell>{t('memberTable.headers.lastName')}</TableCell>
            <TableCell>{t('memberTable.headers.role')}</TableCell>
            <TableCell>{t('memberTable.headers.title')}</TableCell>
            <TableCell>{t('memberTable.headers.email')}</TableCell>
            <TableCell>{t('memberTable.headers.phone')}</TableCell>
            <TableCell align="right">
              {t('memberTable.headers.actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map(member => {
            const visibility = getFieldVisibility(member);
            const displayName =
              member.display_name ||
              member.user?.full_name ||
              member.email ||
              'Pending Invitation';
            const firstName =
              member.user?.first_name || displayName.split(' ')[0] || '';
            const lastName =
              member.user?.last_name ||
              displayName.split(' ').slice(1).join(' ') ||
              '';
            const avatarSrc =
              member.avatar_url || member.user?.avatar_url || undefined;
            const initials = getInitials(firstName, lastName);
            const title = member.user?.profile?.title || '';
            const email =
              member.display_email || member.user?.email || member.email || '';
            const phone = member.user?.phone_number || '';

            return (
              <TableRow
                key={member.id}
                hover
                sx={{
                  cursor: member.user ? 'pointer' : 'default',
                  '&:hover': member.user
                    ? { backgroundColor: 'action.hover' }
                    : {},
                }}
                onClick={event => member.user && handleRowClick(member, event)}
              >
                {/* Avatar */}
                <TableCell>
                  <Avatar
                    src={avatarSrc}
                    alt={displayName}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: getRoleColor(member.role),
                      fontSize: '0.75rem',
                      opacity: member.user ? 1 : 0.7,
                    }}
                  >
                    {!avatarSrc && initials}
                  </Avatar>
                </TableCell>

                {/* First Name */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {firstName}
                  </Typography>
                </TableCell>

                {/* Last Name */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {lastName}
                  </Typography>
                </TableCell>

                {/* Role */}
                <TableCell>
                  <Chip
                    icon={getRoleIcon(member.role)}
                    label={
                      member.role
                        ? t(`roles.${member.role.toLowerCase()}`)
                        : t('memberTable.unknown')
                    }
                    size="small"
                    sx={{
                      bgcolor: `${getRoleColor(member.role)}15`,
                      color: getRoleColor(member.role),
                      fontWeight: 600,
                    }}
                  />
                </TableCell>

                {/* Title */}
                <TableCell>
                  <Typography
                    variant="body2"
                    color={title ? 'text.primary' : 'text.disabled'}
                  >
                    {title || '—'}
                  </Typography>
                </TableCell>

                {/* Email */}
                <TableCell>
                  {renderPrivateField(email, visibility.showEmail)}
                </TableCell>

                {/* Phone */}
                <TableCell>
                  {renderPrivateField(
                    phone,
                    visibility.showPhone,
                    formatPhoneNumber
                  )}
                </TableCell>

                {/* Actions */}
                <TableCell align="right" data-actions-menu>
                  {shouldShowActionsMenu(member) ? (
                    <MemberActionMenu
                      member={member}
                      onRoleChange={(role, reason) =>
                        onRoleChange(member.id, role, reason)
                      }
                      onRemoveMember={reason =>
                        onRemoveMember(member.id, reason)
                      }
                      onViewProfile={() => openMemberProfile(member)}
                      onSendMessage={() =>
                        member.user && onSendMessage(member.user.id)
                      }
                      currentUserRole={currentUserRole || undefined}
                    />
                  ) : (
                    // For view-only users or owner's own row, show nothing or a simple view icon
                    <Box sx={{ width: 24, height: 24 }} />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MemberTable;
